import { computed, type Ref } from 'vue'
import type {
  SignalData, EnrichedSynthNavData, CalendarStatus, UserExecutionStatus,
  UserExecutionConfig, VixIntraday, UserOtpConfig,
} from '../api/client'
import { termStructureStaleVerdict } from './useIntegrityChecks'
import { deriveNavAnchor } from './useNavAnchor'

export interface PipelineStage {
  key: 'raw' | 'xd' | 'fva' | 'signal' | 'gate' | 'band' | 'prev' | 'print' | 'nq' | 'bar'
  label: string
  value: string | null            // formatted, e.g. '+2.42%'
  state: 'active' | 'inactive' | 'blocking' | 'holding' | 'degraded'
  frozen?: boolean                // ❄ marker (FVA frozen snapshot / NQ 08:40 latch)
  provisional?: boolean           // pure step-aside pre-08:40: value still drifting
  detail?: string                 // e.g. 'armed · m_t +0.24% < 0.60% trigger'
  title?: string                  // tooltip
  testid?: string                 // legacy testid to carry (e.g. 'fva-decomp-row')
  explainerKey?: string           // ExplainerDrawer section anchor
}

export interface Anomaly {
  key: string
  // 'benign' = expected-state disclosure (green): not a fault, self-clears
  // without operator action (e.g. FX ref pre-strike fallback each morning).
  severity: 'error' | 'warn' | 'info' | 'benign'
  label: string
  title?: string
  testid?: string
}

// The fund's NAV-strike hour (London local) for display. Mirrors
// FundProfile.nav_strike_uk in signal_core — BGA 10:00, JH 12:00 — pinned
// by src/signal_core/tests/test_phase3_strike_clock.py. The payload doesn't
// carry the strike, so this map is the frontend's single source for it.
export function fxStrikeLabel(fund: string | null | undefined): string {
  return fund === 'JH' ? '12:00' : '10:00'
}

export interface SignalPipelineDeps {
  signal: Ref<SignalData | null>
  enrichedNav: Ref<EnrichedSynthNavData | null>
  activeFundReturnPct: Ref<number | null>
  calendar: Ref<CalendarStatus | null>
  execStatus: Ref<UserExecutionStatus | null>
  execConfig: Ref<UserExecutionConfig | null>
  vixIntraday: Ref<VixIntraday | null>
  otpConfigs: Ref<UserOtpConfig[]>
  systemAlerts: Ref<Array<{ type: string; message: string }>>
  tick: Ref<number>
}

export function useSignalPipeline(deps: SignalPipelineDeps) {
  // Convenience narrowing for BGA-only computeds. Returns the signal
  // narrowed to BgaSignalData when fund='BGA', else null.
  const bgaSignal = computed(() =>
    deps.signal.value && deps.signal.value.fund === 'BGA' ? deps.signal.value : null,
  )

  // Inline freshness badge for the VIX term-structure regime label.
  // Delegates to the shared `termStructureStaleVerdict` helper so the
  // integrity-panel check + this in-context badge stay in lock-step.
  // Time-of-day-aware: tolerates 1-trading-day lag during US pre-market
  // (CBOE VX settles ~21:00 UTC). See helper docstring for the full rule.
  const vixTsStaleBadge = computed(() => {
    const ts = deps.signal.value?.vix_term_structure
    if (!ts) return null
    const verdict = termStructureStaleVerdict(ts.fetch_date)
    if (verdict == null) return null
    const ageDesc = verdict.ageTradingDays === 1
      ? '1 trading day behind, post-settlement'
      : `${verdict.ageTradingDays} trading days behind`
    return {
      severity: verdict.severity,
      label: verdict.label,
      title: `VIX term structure ${ageDesc} (last fetch: ${verdict.fetchDate}). `
        + `Today's regime label is rendered against an older curve. `
        + `Pre-CBOE-settlement (21:00 UTC) a 1-day lag is normal; this badge `
        + `surfaces only when the lag exceeds the expected publishing window.`,
    }
  })

  // --- Derived decision logic ---
  const isBuy = computed(() => deps.signal.value?.decision === 'buy')
  // `hold` is a routine output on JH (no_hold=false: |signal| ≤ threshold) and
  // on BGA filter days. It must NOT fall into the SELL rendering — 2026-07-27
  // a JH hold (+0.05% inside the ±0.89% band) displayed as "SELL — signal
  // positive".
  const isHold = computed(() => deps.signal.value?.decision === 'hold')

  // BGA ex-dividend (30 April) and the day after — both surfaced as a
  // prominent banner because the synth NAV is expected to misprice official
  // NAV by ~200 bps on XD-day (undershoot) and ~160 bps on XD+1 (overshoot)
  // per the 2024+2025 calibration in src/signal_service/xd_correction.py.
  // On XD-day proper the trading blackout filter (ExDividendFilter) also
  // fires; on XD+1 only the synth-NAV correction layer applies — the UI
  // banner there is informational, trading is unblocked.
  const xdEvent = computed(() =>
    deps.calendar.value?.exogenousEvents?.find(
      (e) => e.type === 'ex_dividend' || e.type === 'ex_dividend_plus_one',
    ) ?? null,
  )
  const isXdPlusOne = computed(() => xdEvent.value?.type === 'ex_dividend_plus_one')

  const decisionLabel = computed(() => {
    if (!deps.signal.value) return '--'
    if (isHold.value) return 'HOLD'
    return isBuy.value ? 'BUY' : 'SELL'
  })

  // JH futures step-aside (live arc 2026-07-27): when the promoted preset
  // decides on the 08:40-London NQ overnight print, the decision explanation
  // and a dedicated strip must narrate THAT rule — the band-signal subtitle
  // ("signal positive") is the wrong story and produced the 2026-07-27
  // invisible-BUY incident.
  const stepAside = computed(() => {
    const s = deps.signal.value
    if (!s || !s.step_aside_mode) return null
    return {
      mode: s.step_aside_mode,
      sOn: s.step_aside_s_on ?? null,
      thresholdBps: s.step_aside_threshold_bps ?? 0,
      state: s.step_aside_state ?? null,
      frozenAt: s.step_aside_frozen_at ?? null,
      nqPrint: s.step_aside_nq_print ?? null,
      nqPrevClose: s.step_aside_nq_prev_close ?? null,
    }
  })

  const stepAsideSOnPct = computed(() => {
    const sa = stepAside.value
    if (sa?.sOn == null) return null
    const pct = (sa.sOn * 100).toFixed(2)
    return `${sa.sOn >= 0 ? '+' : ''}${pct}%`
  })

  // Futures rule (pure step-aside) — fund-agnostic since BGA's promotion to
  // bga_futures_driven_v1 (2026-08-29). Every mode-dependent branch in this
  // composable keys off this flag, never off `s.fund`.
  const isPureFuturesMode = computed(() => stepAside.value?.mode === 'pure')

  // Pre-08:40 the print is still drifting: the verdict is provisional.
  const isProvisional = computed(() =>
    isPureFuturesMode.value && stepAside.value?.state === 'live')

  const stepAsideClears = computed(() => {
    const sa = stepAside.value
    if (!sa || sa.sOn == null) return null
    return sa.sOn * 10000 >= sa.thresholdBps
  })

  // Header / verdict pill. The freeze time is the 08:40-London print for both
  // funds (the decision cutoff is 08:40 Europe/London year-round since 2026-10-24); frozen_at is rendered in
  // Europe/London so the pill reads the same in winter.
  const stepAsideStatus = computed<{ state: 'live' | 'frozen' | 'degraded'; label: string } | null>(() => {
    const sa = stepAside.value
    if (!isPureFuturesMode.value || !sa) return null
    if (sa.state === 'frozen') {
      let at = '08:40'
      if (sa.frozenAt) {
        const d = new Date(sa.frozenAt)
        if (!isNaN(d.getTime()))
          at = d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/London' })
      }
      return { state: 'frozen', label: `frozen ${at}` }
    }
    if (sa.state === 'degraded' || sa.sOn == null) return { state: 'degraded', label: 'degraded · no NQ print' }
    return { state: 'live', label: 'live · freezes 08:40' }
  })

  // Dial needle magnitude source. In pure mode the band signal is context
  // only, so sizing the needle from it contradicted the pipeline (a BUY
  // needle sized by a negative synth-NAV signal) — use the NQ move instead.
  const needleInput = computed<{ value: number | null; scale: number }>(() => {
    if (isPureFuturesMode.value) return { value: stepAside.value?.sOn ?? null, scale: 0.005 }
    return { value: deps.signal.value?.signal ?? null, scale: 0.015 }
  })

  const decisionSubtitle = computed(() => {
    const s = deps.signal.value
    if (!s) return ''
    const sig = s.signal ?? 0
    const vix = s.vix ?? 0
    const sa = stepAside.value
    if (sa && !isHold.value) {
      if (sa.sOn == null) return 'no NQ print — stay invested'
      return `NQ overnight ${stepAsideSOnPct.value} ${stepAsideClears.value ? 'clears' : 'below'} ${sa.thresholdBps} bps bar`
    }
    if (isHold.value) {
      // Filter-day holds carry the blocking reason; band holds narrate
      // signal-vs-threshold so a positive-but-small signal reads as inside
      // the dead band rather than contradicting the verdict.
      if (s.gate_reason) return s.gate_reason
      if (s.signal != null && s.threshold != null) {
        const sigPct = (sig >= 0 ? '+' : '') + (sig * 100).toFixed(2)
        return `signal ${sigPct}% inside ±${(s.threshold * 100).toFixed(2)}% band`
      }
      return 'hold'
    }
    // BGA carries imr_wz / wz_threshold / vix_mom_gate — JH does not. On JH we
    // fall back to the signal-only explanation (positive/negative).
    if (s.fund === 'BGA') {
      const wz = s.imr_wz
      const wzThr = s.wz_threshold ?? 0
      if (vix > 35 && (s.vix_mom_gate === 'circuit' || s.vix_mom_gate === 'both')) return 'VIX circuit breaker'
      if (sig > 0 && wz != null && wz > wzThr) return 'signal + momentum confirmed'

      // SELL — build explanation of what's blocking
      const reasons: string[] = []
      if (sig <= 0) reasons.push('signal negative')
      if (wz != null && wz <= wzThr) {
        if (wzThr > 0) {
          reasons.push('momentum blocked — VIX raised bar to ' + wzThr.toFixed(2))
        } else {
          reasons.push('no momentum')
        }
      }
      return reasons.join(' + ') || 'sell'
    }
    // JH (or other future funds): signal-only verdict.
    if (sig > 0) return 'signal positive'
    return sig < 0 ? 'signal negative' : 'sell'
  })

  // --- Synth NAV pence ---
  // Display rule (agreed 2026-04-16):
  //   synth  = anchor × (1 + overnight_return_gbp)
  //   anchor = today's BG official when published, else prev official
  //   pct    = overnight_return_gbp × 100
  // The % shown equals the RET% TOTAL column in the holdings panel (which also
  // reads overnight_return_usd/gbp). Pivoting the anchor to today's official once
  // BG publishes keeps synth anchored to 1430p + basket-drift-since-yesterday.
  const navAnchor = computed(() => deriveNavAnchor({
    todayOfficialNavGbp: deps.enrichedNav.value?.today_official_nav_gbp ?? null,
    prevOfficialNavGbp: deps.enrichedNav.value?.prev_official_nav_gbp ?? null,
  }))

  const anchorPence = computed(() => navAnchor.value.anchorPence)

  const synthNavPence = computed(() => {
    const pct = deps.activeFundReturnPct.value
    if (anchorPence.value == null || pct == null) return null
    return anchorPence.value * (1 + pct / 100)
  })

  // Return shown by the NAV hero: the shadow K_t's live value vs the last
  // official NAV (Dashboard's activeFundReturnPct, 2026-11-04 — BGA/JH's one
  // live value). The Signal gate card in the body still shows the engine's
  // raw→XD→FVA decomposition: that is the decision, not the live value.
  const overnightReturnPct = computed(() => deps.activeFundReturnPct.value)

  // Pre-FVA basket/synth return — still the right number for the NAV info
  // popups ("basket return" is a holdings-space quantity the FVA policy
  // correction is not part of) and for the K_t vs aggregate delta.
  const basketReturnPct = computed(() => {
    const fin = deps.signal.value?.synth_nav_return_gbp
    if (fin != null) return fin * 100
    return deps.activeFundReturnPct.value
  })

  // 2026-05-17: K_t-derived overnight return (telemetry, computed live by
  // signal_service.kt_synth_nav whenever K_t input + morning anchor are both
  // available). Shown side-by-side with the headline in the source strip so
  // the operator can compare A vs B before / during / after promotion.
  const ktReturnPct = computed(() => {
    // K_t telemetry is BGA-only (synth NAV decomposition lives in the BGA engine).
    const s = bgaSignal.value
    const v = s?.kt_synth_nav_return_gbp
    return v != null ? v * 100 : null
  })

  // XD anticipation: BGA's annual ex-dividend correction (~+130 bps on XD day,
  // ~-160 bps on XD+1) is applied inside synth_nav.compute_enriched_update,
  // AFTER the FX conversion but BEFORE the value is stored as
  // synth_nav_return_gbp_raw. So on XD days the "raw" value already includes
  // the XD contribution, which is misleading — surface it as its own line and
  // strip it from the displayed raw figure so the three rows really do
  // decompose the final signal.
  const xdApplied = computed(() => deps.enrichedNav.value?.xd_correction_applied === true)
  const xdCorrectionPct = computed(() => {
    const bps = deps.enrichedNav.value?.xd_correction_bps
    return xdApplied.value && bps != null ? bps / 100 : null
  })
  const xdCorrectionLabel = computed(() => deps.enrichedNav.value?.xd_correction_label ?? null)

  const rawReturnPct = computed(() => {
    // synth_nav_return_gbp_raw is the post-XD raw synth return.
    // For the decomposition panel we strip the XD contribution so the row
    // labelled "raw basket move" really is the raw basket move.
    const raw = deps.signal.value?.synth_nav_return_gbp_raw
    if (raw == null) return null
    const xd = xdCorrectionPct.value
    return xd != null ? raw * 100 - xd : raw * 100
  })
  // --- FVA gap-correction (live since 2026-07-14, databento_kt_fva_tuned_v1) ---
  // engine.py subtracts fva_correction from the signal AFTER selecting the
  // synth-NAV return, so signal = synth_nav_return_gbp − fva_correction. The
  // decomposition row displays the signed CONTRIBUTION (−fva_correction) so
  // the rows keep summing to the headline Signal value.
  const fvaActive = computed(() => {
    const s = bgaSignal.value
    return s?.fva_mode === 'gap_correct' && s.fva_correction != null
      && Math.abs(s.fva_correction) >= 0.00005
  })
  // Degraded = inputs missing (prev-close anchor or m_lag); the correction is
  // deliberately inert rather than wrong — surface it so the operator knows
  // the signal is temporarily UNcorrected while the preset assumes correction.
  const fvaDegraded = computed(() =>
    bgaSignal.value?.fva_mode === 'gap_correct' && bgaSignal.value?.fva_state === 'degraded')
  const fvaContributionPct = computed(() => {
    const c = bgaSignal.value?.fva_correction
    return c != null ? -c * 100 : null
  })

  // --- Dynamic "Now:" narratives for the info popups (2026-07-14) ---
  // Every (i) popup opens with today's story told in live numbers, assembled
  // from the same computeds the decomposition rows render — popup and rows
  // can never drift apart.
  const fmtPctSigned = (v: number, dp = 2) => (v >= 0 ? '+' : '') + v.toFixed(dp) + '%'

  const signalNarrative = computed(() => {
    const fin = deps.signal.value?.signal
    if (fin == null) return null
    const verdict = fin > 0 ? 'BUY candidate (momentum gate decides)' : 'SELL'
    const parts: string[] = []
    if (rawReturnPct.value != null) parts.push(`raw basket move ${fmtPctSigned(rawReturnPct.value)}`)
    if (xdApplied.value && xdCorrectionPct.value != null) parts.push(`XD anticipation ${fmtPctSigned(xdCorrectionPct.value)}`)
    if (fvaActive.value && fvaContributionPct.value != null) parts.push(`FVA gap correction ${fmtPctSigned(fvaContributionPct.value)}`)
    if (!parts.length) return `signal ${fmtPctSigned(fin * 100)} → ${verdict}`
    return `${parts.join(', ')} sum to ${fmtPctSigned(fin * 100)} → ${verdict}`
  })

  const imrNarrative = computed(() => {
    const s = bgaSignal.value
    const wz = s?.imr_wz
    if (wz == null) return null
    const thr = s?.wz_threshold ?? 0
    const pass = wz > thr
    return `wz ${(wz >= 0 ? '+' : '') + wz.toFixed(2)} vs effective threshold ${thr.toFixed(2)} → gate ${
      pass ? 'passes — momentum confirms a BUY' : 'blocks — a positive signal is demoted to SELL'}`
  })

  const fvaNarrative = computed(() => {
    const s = bgaSignal.value
    if (s?.fva_mode !== 'gap_correct' || s.fva_correction == null) return null
    const trig = ((s.fva_trigger_lo_bps ?? 60) / 10000) // published lo trigger as a decimal (same fallback as fvaStage)
    const trigTxt = (trig * 100).toFixed(2) + '%'
    const bits: string[] = []
    if (s.fva_m_t != null) {
      bits.push(Math.abs(s.fva_m_t) > trig
        ? `today's overnight move ${fmtPctSigned(s.fva_m_t * 100)} is beyond the ${trigTxt} trigger — BG part-adjusts today's strike for it`
        : `today's overnight move ${fmtPctSigned(s.fva_m_t * 100)} is below the ${trigTxt} trigger — no adjustment at today's strike`)
    }
    if (s.fva_m_lag != null) {
      bits.push(Math.abs(s.fva_m_lag) > trig
        ? `yesterday's ${fmtPctSigned(s.fva_m_lag * 100)} was fair-valued at yesterday's strike and mechanically reverses out today`
        : `yesterday's ${fmtPctSigned(s.fva_m_lag * 100)} was below trigger, so nothing reverses`)
    }
    const net = `net ${fmtPctSigned(-s.fva_correction * 100)} applied to the signal`
    return bits.length ? `${bits.join('; ')} — ${net}.` : `${net}.`
  })

  // --- Auto-execute countdown (weekend + holiday aware) ---
  // Auto-execute is ON/OFF (2026-09-30): BGA deals at a fixed 08:41 UK, one
  // tick after the 08:40:30 UK dealing-plan publish — no per-user time.
  // All arithmetic runs in London wall-clock space (a Date whose UTC clock
  // == London wall clock, same trick as GoldSignalPanel.londonNow) so the
  // target stays at 08:41 UK across BST/GMT.
  const AUTO_EXEC_UK = { hour: 8, minute: 41 }

  function londonWallClock(d: Date): Date {
    const parts = new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Europe/London',
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
      hour12: false,
    }).formatToParts(d)
    const map: Record<string, string> = {}
    for (const p of parts) if (p.type !== 'literal') map[p.type] = p.value
    return new Date(Date.UTC(
      Number(map.year), Number(map.month) - 1, Number(map.day),
      Number(map.hour) % 24, Number(map.minute), Number(map.second)))
  }

  /** Next 08:41 UK auto-execute slot, in London wall-clock space. */
  function nextCutoffTarget(now: Date): Date {
    const { hour, minute } = AUTO_EXEC_UK

    const today = new Date(now)
    today.setUTCHours(hour, minute, 0, 0)

    // If today is a trading day and the slot hasn't passed, use today
    const cal = deps.calendar.value
    if (cal?.tradingDay && today.getTime() > now.getTime()) {
      return today
    }

    // Otherwise find next trading day from calendar API or skip weekends
    if (cal?.nextTradingDay) {
      const parts = cal.nextTradingDay.split('-').map(Number)
      return new Date(Date.UTC(parts[0], parts[1] - 1, parts[2], hour, minute, 0, 0))
    }

    // Fallback: skip forward past weekends
    const target = new Date(now)
    target.setUTCHours(hour, minute, 0, 0)
    if (target.getTime() <= now.getTime()) target.setUTCDate(target.getUTCDate() + 1)
    while (target.getUTCDay() === 0 || target.getUTCDay() === 6) {
      target.setUTCDate(target.getUTCDate() + 1)
    }
    return target
  }

  const countdown = computed(() => {
    void deps.tick.value
    const now = londonWallClock(new Date())
    const target = nextCutoffTarget(now)
    const diff = target.getTime() - now.getTime()
    if (diff <= 0) return 'PASSED'

    const days = Math.floor(diff / 86400000)
    const hrs = Math.floor((diff % 86400000) / 3600000)
    const mins = Math.floor((diff % 3600000) / 60000)
    const secs = Math.floor((diff % 60000) / 1000)

    if (days > 0) return `${days}d ${hrs}h ${mins}m`
    if (hrs > 0) return `${hrs}h ${mins}m ${secs}s`
    return `${mins}m ${secs}s`
  })

  // --- Gate computeds ---
  const signalGatePassed = computed(() => (deps.signal.value?.signal ?? 0) > 0)

  // Stale-payload detection (2026-07-19): the weekend/market-closed publish
  // gate (c9e1e0de) deliberately freezes latest:signal, so every value on this
  // panel — most confusingly the wz momentum gate — stops moving. Without a
  // badge that reads as "broken" (2026-07-18: an hour chased as a preset
  // defect). Badge after 30 min of payload silence.
  const STALE_PAYLOAD_MS = 30 * 60 * 1000
  const signalStaleAsOf = computed(() => {
    const ts = deps.signal.value?.timestamp
    if (!ts) return null
    const parsed = Date.parse(/[zZ]|[+-]\d\d:?\d\d$/.test(ts) ? ts : ts + 'Z')
    if (Number.isNaN(parsed) || Date.now() - parsed < STALE_PAYLOAD_MS) return null
    return ts.slice(0, 16).replace('T', ' ') + 'Z'
  })

  const momentumGatePassed = computed(() => {
    // IMR momentum gate is BGA-only (imr_wz / wz_threshold live on BgaSignalData).
    const s = bgaSignal.value
    const wz = s?.imr_wz
    if (wz == null) return false
    const thr = s?.wz_threshold ?? 0
    return wz > thr
  })

  const momentumGateBreakdown = computed(() => {
    const s = bgaSignal.value
    if (!s || s.vix == null) return null

    const vix = s.vix
    const ts = s.vix_term_structure
    const contangoPct = ts?.spot_vs_m1_pct ?? ts?.contango_pct ?? 0
    const wz = s.imr_wz ?? 0
    const vmgMode = s.vix_mom_gate ?? 'off'
    const signalVal = s.signal ?? 0

    // VMG mode flags — the mode decides which components RAISE the threshold.
    // 2026-07-18 correction: the wz gate itself is enforced in EVERY mode
    // (both engines demote to SELL whenever wz <= threshold − dead-zone, with
    // the threshold simply staying 0 for 'off' and 'circuit') — this widget
    // previously claimed off/circuit "don't enforce", showing PASS on days
    // the engine demoted. Mirrors ParameterisedStrategy.GetVixMomThreshold
    // and engine.py's _compute_wz_threshold:
    //   off:    threshold stays 0 (wz must still be positive)
    //   regime: VIX-regime tiers raise it
    //   term:   term-structure tiers raise it
    //   both:   max(regime, term)
    //   circuit: threshold stays 0; VIX>35 additionally forces SELL.
    const regimeActive = vmgMode === 'regime' || vmgMode === 'both'
    const termActive = vmgMode === 'term' || vmgMode === 'both'
    const circuitActive = vmgMode === 'circuit' || vmgMode === 'both'

    // Component values. Computed regardless so the UI can show "what it would
    // be if this mode were active", but `*Active` flags drive whether they
    // contribute to the actual gate decision.
    let regimeThreshold = 0
    let regimeLabel = 'low'
    if (vix >= 35) { regimeThreshold = 0.20; regimeLabel = 'extreme' }
    else if (vix >= 25) { regimeThreshold = 0.10; regimeLabel = 'high' }
    else if (vix >= 15) { regimeThreshold = 0; regimeLabel = 'normal' }
    else { regimeThreshold = 0; regimeLabel = 'low' }

    let termThreshold = 0
    let termLabel = 'neutral'
    if (contangoPct <= -3) { termThreshold = 0; termLabel = 'deep contango' }
    else if (contangoPct <= 0) { termThreshold = 0; termLabel = 'mild contango' }
    else if (contangoPct <= 3) { termThreshold = 0.05; termLabel = 'mild backwardation' }
    else { termThreshold = 0.15; termLabel = 'deep backwardation' }

    // Source-of-truth for the threshold the gate is ACTUALLY using comes from
    // signal_service in s.wz_threshold (already includes the dead-zone
    // adjustment). Fall back to the recomputed combined threshold only when
    // the live payload doesn't include it (older signal_service).
    const recomputedCombined = (regimeActive ? regimeThreshold : 0) > (termActive ? termThreshold : 0)
      ? (regimeActive ? regimeThreshold : 0)
      : (termActive ? termThreshold : 0)
    const combinedThreshold = s.wz_threshold ?? recomputedCombined
    const circuitTriggered = circuitActive && vix > 35

    // The wz bar is enforced in every mode; regime/term components only
    // raise it above the baseline 0. `raisedBarActive` drives the "which
    // components contribute" explainer, NOT whether the gate applies.
    const raisedBarActive = regimeActive || termActive
    const wzPasses = wz > combinedThreshold
    const gatePasses = wzPasses && !circuitTriggered
    const gateBlocked = signalVal > 0 && !gatePasses
    const headroom = wz - combinedThreshold

    return {
      vmgMode, vix,
      regimeLabel, regimeThreshold, regimeActive,
      contangoPct, termLabel, termThreshold, termActive,
      circuitActive, circuitTriggered,
      combinedThreshold, raisedBarActive,
      wz, gatePasses, gateBlocked, headroom,
    }
  })

  // --- Context pills ---
  const nas100MarketReturn = computed(() => {
    if (!deps.signal.value?.nas100_price || !deps.signal.value?.nas100_prev_close_t1) return null
    return (deps.signal.value.nas100_price / deps.signal.value.nas100_prev_close_t1) - 1
  })

  const nas100Return = computed(() => {
    if (!deps.signal.value?.nas100_price) return null
    // Use the 10:00 UK morning anchor (BGA strike) for the signal pill —
    // pinned by SynthNavEngine.freeze_10am_baseline at the daily strike and
    // never rotated mid-window. Falls back to nq_nav_prev_close (the legacy
    // T-2 field, which used to double as morning anchor pre-2026-05-07) so
    // older backend builds still render. See
    // `project_signal_nas100_anchor_drift_defect.md`.
    const anchor = deps.signal.value.nas100_morning_anchor ?? deps.signal.value.nq_nav_prev_close
    if (!anchor || anchor <= 0) return null
    return (deps.signal.value.nas100_price / anchor) - 1
  })

  const gbpusdReturn = computed(() => {
    if (!deps.signal.value?.gbpusd_rate || !deps.signal.value?.gbpusd_ref) return null
    return (deps.signal.value.gbpusd_rate / deps.signal.value.gbpusd_ref) - 1
  })

  const gbpusdMarketReturn = computed(() => {
    if (!deps.signal.value?.gbpusd_rate || !deps.signal.value?.gbpusd_prev_close_t1) return null
    return (deps.signal.value.gbpusd_rate / deps.signal.value.gbpusd_prev_close_t1) - 1
  })

  // True when today's 10am UK FX rate hasn't yet been captured by fx_manager.
  // In that case the backend (signal_service/main.py) falls back to prev_10am
  // for gbpusd_ref, making the "signal" pill numerically identical to the
  // "vs close" pill — collapse-to-same-value defect. UI surfaces this with
  // a ⚠ marker + a "= vs close (pre-10am)" badge rather than rendering two
  // identical pills. The backend ships an explicit `gbpusd_ref_source`
  // discriminator ("today_10am" | "prev_10am") to avoid heuristic detection.
  const gbpusdRefDegraded = computed(() => {
    return deps.signal.value?.gbpusd_ref_source === 'prev_10am'
  })

  const fxWindPill = computed(() => {
    const rate = deps.signal.value?.gbpusd_rate
    const gbpRef = deps.signal.value?.gbpusd_ref
    if (rate == null || gbpRef == null || gbpRef <= 0) return null
    const changePct = ((rate / gbpRef) - 1) * 100
    if (Math.abs(changePct) < 0.01) return null
    const abs = Math.abs(changePct).toFixed(2)
    if (changePct > 0) return { label: `Headwind +${abs}%`, cls: 'pill-negative', title: 'GBP strengthening vs USD' }
    return { label: `Tailwind -${abs}%`, cls: 'pill-positive', title: 'GBP weakening vs USD' }
  })

  const vixDelta = computed(() => deps.vixIntraday.value?.change_pct != null
    ? deps.vixIntraday.value.change_pct * 100 : null)

  // OTP day-type — sourced from the COMMITTED execution decision, not the
  // live (intraday-fluttering) signal. Mirrors `OtpMonitorService.cs:259-260`
  // which already routes OTP gating off `user_execution_status.decision`
  // (frozen at the 07:50 UTC cutoff) after the 2026-04-22 incident where
  // the live signal flipped SELL→BUY at 12:17 and would have applied
  // buy-day sizing to a user who actually sold in the morning.
  //
  // Fallback to the live signal when no committed decision exists yet —
  // pre-cutoff state, or BGA-XD blackout where the BGA decision is HOLD
  // (dividend mechanics) but OTPs aren't subject to those mechanics
  // (per `feedback_signal_decoupled_from_filters` 2026-04-30).
  // Normalise the committed execution decision exactly the way the C#
  // executor does in `OtpMonitorService.ResolveDecisionFromStatus`:
  //  - lowercase the value (the persistence layer stores "BUY"/"SELL"
  //    uppercase),
  //  - validate updatedAt is *today* (stale rows from a prior trading
  //    day must not be honoured).
  const committedDecision = computed<'buy' | 'sell' | null>(() => {
    const status = deps.execStatus.value
    const raw = status?.decision?.toLowerCase()
    if (raw !== 'buy' && raw !== 'sell') return null
    if (!status?.updatedAt) return null
    // Compare ISO date parts — the executor uses UK-today, but UTC-today
    // disagrees only between 23:00 UTC and midnight UTC during BST (post-
    // session, no OTP activity), and the timestamp itself is UTC-anchored.
    const todayIso = new Date().toISOString().slice(0, 10)
    const rowIso = status.updatedAt.slice(0, 10)
    return rowIso === todayIso ? raw : null
  })

  const otpDayType = computed<'buy' | 'sell' | null>(() => {
    const committed = committedDecision.value
    if (committed) return committed

    const dec = deps.signal.value?.decision
    if (dec === 'buy') return 'buy'
    if (dec === 'sell') return 'sell'
    if (dec !== 'hold') return null
    const reason = deps.signal.value?.gate_reason ?? ''
    const sig = deps.signal.value?.signal
    // XD-exempt fallback: BGA HOLD with Ex-dividend gate AND a usable signal value
    if (reason.startsWith('Ex-dividend') && sig != null && sig !== 0) {
      return sig > 0 ? 'buy' : 'sell'
    }
    return null
  })

  // Divergence between committed execution decision and the live signal —
  // surfaced in the OTP table footer so it's obvious *why* a row is greyed
  // when the live signal direction would suggest otherwise. The OTP
  // executor honours the committed decision; this label tells the operator
  // the live signal has flipped since cutoff (interesting for tomorrow's
  // strike but does not change today's OTP gating).
  const otpDivergence = computed<{ committed: 'buy' | 'sell'; live: 'buy' | 'sell' } | null>(() => {
    const committed = committedDecision.value
    const live = deps.signal.value?.decision?.toLowerCase()
    if (committed && (live === 'buy' || live === 'sell') && committed !== live) {
      return { committed, live: live as 'buy' | 'sell' }
    }
    return null
  })

  const otpExemptActive = computed(() => {
    const dec = deps.signal.value?.decision
    const reason = deps.signal.value?.gate_reason ?? ''
    return dec === 'hold'
      && reason.startsWith('Ex-dividend')
      && otpDayType.value !== null
  })

  const otpByType = (type: string) => deps.otpConfigs.value.find(c => c.otpType === type)

  function otpBar(otpType: string) {
    const cfg = otpByType(otpType)
    const vixChangePct = vixDelta.value
    if (vixChangePct == null || cfg == null) return null
    const threshold = cfg.triggerThreshold
    const absThreshold = Math.abs(threshold)
    // For hedge/short_conv: VIX rising is progress toward trigger
    // For overlay/long_conv: VIX falling is progress toward trigger
    const relevantChange = (otpType === 'overlay' || otpType === 'long_conv') ? -vixChangePct : vixChangePct
    // Square root scale: small values are visible, large values don't overflow
    const sqrtThr = Math.sqrt(absThreshold)
    const barMax = sqrtThr * 1.3
    const thrPct = Math.min((sqrtThr / barMax) * 100, 100)
    let fillPct: number
    if (relevantChange >= 0) {
      // Moving toward trigger — show actual progress
      fillPct = Math.min((Math.sqrt(relevantChange) / barMax) * 100, 100)
    } else {
      // Moving away — small bar capped well below threshold
      fillPct = Math.min((Math.sqrt(Math.abs(relevantChange)) / barMax) * 25, thrPct * 0.25)
    }
    // Show direction: positive = moving toward trigger, negative = moving away
    const movingToward = relevantChange > 0
    return {
      fillPct,
      thrPct,
      label: (vixChangePct >= 0 ? '+' : '') + vixChangePct.toFixed(1) + '%',
      enabled: cfg.enabled,
      greyed: (otpType === 'hedge' || otpType === 'long_conv')
        ? otpDayType.value !== 'buy'
        : otpDayType.value !== 'sell',
      movingToward,
    }
  }

  // VIX regime label — preserved (the inline term-structure SVG was retired
  // 2026-05-03 in favour of the dedicated /charts/vixregime view, which
  // renders the full curve + history with TradingView Lightweight Charts).
  // SignalPanel keeps the regime badge and structure label only — all
  // curve / chart state moved to VixRegimeChart.vue.
  const regimeLabel = computed(() => {
    if (!deps.signal.value?.vix_regime) return '--'
    return deps.signal.value.vix_regime.charAt(0).toUpperCase() + deps.signal.value.vix_regime.slice(1)
  })

  const fmtSignedPct = (v: number | null | undefined) => {
    if (v == null) return '--'
    const pct = v * 100
    return (pct >= 0 ? '+' : '') + pct.toFixed(2) + '%'
  }

  // --- NEW: permanent-stage pipeline model (spec Layer 2) ---
  const fvaStage = computed<PipelineStage>(() => {
    const s = deps.signal.value
    const base = { key: 'fva' as const, label: 'FVA', testid: 'fva-decomp-row', explainerKey: 'fva' }
    if (!s || s.fva_mode !== 'gap_correct') {
      return { ...base, value: null, state: 'inactive', detail: 'off',
        title: 'FVA gap correction not active in the live preset' }
    }
    const frozen = s.fva_state === 'frozen'
    if (s.fva_state === 'degraded') {
      return { ...base, value: null, state: 'degraded', detail: 'DEGRADED · inert',
        title: 'FVA inputs unavailable (prev-close anchor or yesterday\'s overnight move) — correction is inert this tick. The signal is temporarily uncorrected; check signal container logs.' }
    }
    const corr = s.fva_correction
    if (corr != null && Math.abs(corr) >= 0.00005) {
      return { ...base, value: fmtPctSigned(-corr * 100), state: 'active', frozen,
        title: frozen ? 'Decision-time snapshot republished outside the live window' : undefined }
    }
    const trigPct = ((s.fva_trigger_lo_bps ?? 60) / 100)
    const mt = s.fva_m_t
    const detail = mt != null
      ? `armed · m_t ${fmtPctSigned(mt * 100)} < ${trigPct.toFixed(2)}% trigger`
      : s.fva_state === 'not_applicable' ? 'armed · no live window data' : 'armed'
    return { ...base, value: null, state: 'inactive', frozen, detail,
      title: 'Overnight move below the BG fair-valuation trigger — no correction expected at today\'s strike' }
  })

  const stages = computed<PipelineStage[]>(() => {
    const s = deps.signal.value
    if (!s) return []
    const holdReason = s.decision === 'hold' ? (s.gate_reason ?? null) : null

    // JH pure-mode step-aside: the pipeline IS the NQ rule (spec Layer 2).
    const sa = stepAside.value
    if (sa && sa.mode === 'pure') {
      // The two inputs lead so the NQ % is traceable without opening the drawer.
      const fmtPx = (v: number | null) => v != null ? v.toLocaleString('en-GB', { maximumFractionDigits: 2 }) : null
      const prev: PipelineStage = { key: 'prev', label: 'prev close', value: fmtPx(sa.nqPrevClose),
        state: sa.nqPrevClose != null ? 'active' : 'inactive', detail: sa.nqPrevClose == null ? 'unavailable' : undefined,
        explainerKey: 'step-aside' }
      const print: PipelineStage = { key: 'print', label: '08:40 print', value: fmtPx(sa.nqPrint),
        state: sa.nqPrint != null ? 'active' : 'inactive', detail: sa.nqPrint == null ? 'unavailable' : undefined,
        provisional: sa.state === 'live', explainerKey: 'step-aside' }
      const nq: PipelineStage = sa.sOn == null
        ? { key: 'nq', label: 'NQ overnight', value: null, state: 'degraded',
            detail: 'no NQ print — fail-safe: stay invested', testid: 'step-aside-strip', explainerKey: 'step-aside' }
        : { key: 'nq', label: 'NQ overnight', value: stepAsideSOnPct.value,
            state: 'active', frozen: sa.state === 'frozen', provisional: sa.state === 'live',
            testid: 'step-aside-strip', explainerKey: 'step-aside' }
      const bar: PipelineStage = { key: 'bar', label: `${sa.thresholdBps} bps bar`,
        value: sa.sOn == null ? null : (stepAsideClears.value ? 'clears' : 'below'),
        state: holdReason ? 'holding' : (isBuy.value || sa.sOn == null ? 'active' : 'blocking'),
        detail: holdReason ?? (s.signal != null ? `band signal ${fmtSignedPct(s.signal)} (context only)` : undefined),
        explainerKey: 'step-aside' }
      return [prev, print, nq, bar]
    }

    const out: PipelineStage[] = []
    out.push({ key: 'raw', label: 'raw basket',
      value: rawReturnPct.value != null ? fmtPctSigned(rawReturnPct.value) : null,
      state: rawReturnPct.value != null ? 'active' : 'inactive', explainerKey: 'signal' })
    out.push(xdApplied.value && xdCorrectionPct.value != null
      ? { key: 'xd', label: 'XD', value: fmtPctSigned(xdCorrectionPct.value), state: 'active',
          detail: xdCorrectionLabel.value ?? undefined, testid: 'xd-stage', explainerKey: 'xd' }
      : { key: 'xd', label: 'XD', value: null, state: 'inactive', detail: 'off', explainerKey: 'xd' })
    out.push(fvaStage.value)
    out.push({ key: 'signal', label: 'signal', value: s.signal != null ? fmtSignedPct(s.signal) : null,
      state: s.signal != null ? 'active' : 'inactive', explainerKey: 'signal' })

    if (s.decision === 'hold') {
      // Band hold (threshold present, no gate_reason) or filter-day hold.
      const detail = holdReason
        ?? (s.signal != null && s.threshold != null
          ? `signal ${fmtSignedPct(s.signal)} inside ±${(s.threshold * 100).toFixed(2)}% band` : 'hold')
      out.push({ key: 'band', label: holdReason ? 'filter' : 'band', value: null,
        state: 'holding', detail, explainerKey: 'signal' })
      return out
    }

    // Momentum gate stage — BGA only (JH band mode has no wz gate).
    const b = bgaSignal.value
    if (b && b.imr_wz != null) {
      const pass = momentumGatePassed.value
      const blocked = (s.signal ?? 0) > 0 && !pass
      out.push({ key: 'gate', label: 'wz gate',
        value: pass ? 'pass' : `wz ${fmtPctSigned(b.imr_wz, 2).replace('%', '')} blocks`,
        state: blocked ? 'blocking' : pass ? 'active' : 'inactive',
        detail: `wz ${(b.imr_wz >= 0 ? '+' : '') + b.imr_wz.toFixed(2)} vs ${(b.wz_threshold ?? 0).toFixed(2)}`,
        explainerKey: 'imr' })
    }
    return out
  })

  const decision = computed<'buy' | 'sell' | 'hold' | null>(() => {
    const d = deps.signal.value?.decision
    return d === 'buy' || d === 'sell' || d === 'hold' ? d : null
  })

  const anomalies = computed<Anomaly[]>(() => {
    const s = deps.signal.value
    const out: Anomaly[] = []
    for (const a of deps.systemAlerts.value)
      out.push({ key: `system-alert-${a.type}`, severity: 'error', label: a.message })
    if (s?.error) out.push({ key: 'signal-error', severity: 'error', label: s.error })
    if (s?.prev_close_stale) out.push({ key: 'stale-baseline', severity: 'error',
      label: `STALE BASELINE (${s.prev_close_date})`,
      title: 'Baseline data is stale — nightly data refresh may have failed' })
    if (s?.fva_mode === 'gap_correct' && s.fva_state === 'degraded')
      out.push({ key: 'fva-degraded', severity: 'warn', label: 'FVA DEGRADED — signal uncorrected',
        testid: 'fva-degraded-badge',
        title: 'FVA inputs unavailable — correction inert this tick; check signal container logs' })
    if (signalStaleAsOf.value) out.push({ key: 'stale-payload', severity: 'warn',
      label: `payload frozen — as of ${signalStaleAsOf.value}`, testid: 'wz-stale-badge',
      title: 'latest:signal is frozen by the market-closed publish gate — values resume with the next session\'s ticks. This is by design, not a fault.' })
    if (otpDivergence.value) out.push({ key: 'otp-divergence', severity: 'warn',
      label: `committed ${otpDivergence.value.committed.toUpperCase()} · live ${otpDivergence.value.live.toUpperCase()} — OTPs honour committed` })
    if (s?.nq_t2_degraded) out.push({ key: 'nq-t2-degraded', severity: 'warn',
      label: 'NAS100 T-2 close missing — T-1 fallback (signal = vs close)' })
    if (gbpusdRefDegraded.value) out.push({ key: 'fx-ref-fallback', severity: 'benign',
      label: `FX ref pre-${fxStrikeLabel(s?.fund)} fallback (signal baseline = vs close)`,
      title: `Routine pre-strike state — today's ${fxStrikeLabel(s?.fund)} London FX fix isn't captured yet, so the signal baseline falls back to yesterday's fix (same as vs close). Clears automatically at the strike.` })
    if (vixTsStaleBadge.value) out.push({ key: 'vix-ts-stale',
      severity: vixTsStaleBadge.value.severity === 'fail' ? 'error' : 'warn',
      label: `VIX curve ${vixTsStaleBadge.value.label}`, title: vixTsStaleBadge.value.title })
    for (const evt of deps.calendar.value?.exogenousEvents ?? [])
      out.push({ key: `exo-${evt.type}`, severity: 'info',
        label: `${evt.label} ${evt.active ? 'ON' : 'OFF'}`, title: evt.description })
    const rank = { error: 0, warn: 1, info: 2, benign: 3 } as const
    return out.sort((a, b) => rank[a.severity] - rank[b.severity])
  })

  return {
    bgaSignal, isBuy, isHold, decisionLabel, decisionSubtitle, stepAside,
    stepAsideSOnPct, isPureFuturesMode, isProvisional, stepAsideStatus,
    needleInput, xdEvent, isXdPlusOne, vixTsStaleBadge, anchorPence,
    synthNavPence, overnightReturnPct, basketReturnPct, ktReturnPct,
    xdApplied, xdCorrectionPct, xdCorrectionLabel, rawReturnPct, fvaActive,
    fvaDegraded, fvaContributionPct, signalNarrative, imrNarrative,
    fvaNarrative, countdown, signalGatePassed, signalStaleAsOf,
    momentumGatePassed, momentumGateBreakdown, nas100Return,
    nas100MarketReturn, gbpusdReturn, gbpusdMarketReturn, gbpusdRefDegraded,
    fxWindPill, vixDelta, committedDecision, otpDayType, otpDivergence,
    otpExemptActive, otpByType, otpBar, regimeLabel, fmtSignedPct,
    stages, decision, anomalies,
  }
}

export type MomentumGateBreakdown = NonNullable<ReturnType<typeof useSignalPipeline>['momentumGateBreakdown']['value']>
export type StepAsideView = NonNullable<ReturnType<typeof useSignalPipeline>['stepAside']['value']>
export type StepAsideStatus = NonNullable<ReturnType<typeof useSignalPipeline>['stepAsideStatus']['value']>
export type OtpBarState = NonNullable<ReturnType<ReturnType<typeof useSignalPipeline>['otpBar']>>
